/* eslint-disable @next/next/no-img-element */
import React, { useRef, useEffect } from "react";
import { IoCalendar } from "react-icons/io5";
import { getFormattedDate } from "@/app/shared/utils/utils";
import { IFeaturedPosts } from "@/app/shared/interfaces";
import Image from "next/image";
import Link from "next/link";
import { motion, useAnimation, useInView } from "framer-motion";

interface IComponentProps extends IFeaturedPosts {
  idx?: number;
}

const BlogPostCard: React.FC<IComponentProps> = ({
  thumbnailSrc,
  title,
  slug,
  excerpt,
  date: postDate,
  idx
}: IComponentProps): JSX.Element => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true});
  const controls = useAnimation();

  const transitionDuration = idx && idx >= 6 ? 0.8 : 0;
  const loading = (idx && idx >= 6) ? "lazy" : "eager";

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      className="flex flex-col bg-slate-800/50 rounded-lg border-2 border-slate-200/10 group-hover:border-slate-200/30 shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
      animate={controls}
      viewport={{ once: true }}
      initial="hidden"
      transition={{
        type: "spring",
        delay: 0,
        duration: transitionDuration,
      }}
      whileInView="visible"
      variants={{
        visible: {
          rotateX: 0,
          opacity: 1,
          scale: 1,
        },
        hidden: {
          rotateX: 10,
          opacity: 0,
          scale: 0.7,
        },
      }}
    >
      <div className="flex flex-col justify-between flex-1">
        <h3 className="font-medium leading-snug text-slate-200">
          <div>
            <Link
              className="items-baseline font-medium leading-tight text-slate-200 hover:text-blue-300 focus-visible:text-blue-300 text-base"
              href={`/blog/${slug}`}
              rel="canonical"
              aria-label={title}
            >
              <Image
                alt={title}
                src={thumbnailSrc}
                loading={loading}
                width={352}
                height={192}
                decoding="async"
                className="w-full object-cover"
                style={{ color: "transparent" }}
              />
              <div>
                <div>
                  <span className="block mt-2 text-xl font-semibold py-2 px-6">
                    {title}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </h3>
        <div className="px-6">
          <p className="mt-3 text-base">{excerpt}</p>
          <div className="flex mt-12 items-center justify-between py-6">
            <div className="text-sm">
              <IoCalendar className="mr-2 inline-block" style={{ marginBottom: "3.5px" }} />
              {getFormattedDate(postDate)}
            </div>
            <div className="flex-shrink-0">
              <Link
                href={`/blog/${slug}`}
                className="block rounded bg-gradient-to-br from-blue-400 via-blue-500 to-purple-600 px-2 py-1 text-sm tracking-widest text-white cursor-pointer lg:hover:from-blue-500 lg:hover:via-blue-600 lg:hover:to-purple-700"
                aria-label={`Read more about ${title}`}
              >
                Read More
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPostCard;
